import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {FormRow} from '@dropbox/dig-components/form_row';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import type {UIIconProps} from '@dropbox/dig-icons';
import {UIIcon} from '@dropbox/dig-icons';
import * as KnowledgeLinkIcons from '@dropbox/dig-icons/assets';
import {AiSearchLine, ChevronLeftLine, ChevronRightLine, LinkLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {aiDrawerViewAtom, aiSearchComposerDraftAtom, openAiSearchAndAskAtom} from 'atoms/aiSearch';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {isMobileAtom} from 'atoms/layout';
import {showSearchAtom} from 'atoms/search';
import {HomeService, type KnowledgeLink} from 'client';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {Link} from 'components/DSYS/Link';
import {HomeSearchField} from 'components/search/box/SearchField';
import {t} from 'i18next';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type UIEvent as ReactUIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {getService} from 'utilities';
import {logDashboardInteraction} from 'views/home/dashboard/dashboardAnalytics';
import {
  HOME_KNOWLEDGE_LINKS_QUERY_KEY,
  KNOWLEDGE_LINKS_STALE_TIME_MS,
} from 'views/home/homeQueryKeys';

import styles from './Search.module.css';

export const SearchSection = ({
  source = 'home',
}: {
  /** SEARCH_SHOWN's `source` when the box opens search. */
  source?: 'home' | 'home_directory';
} = {}) => {
  const [showSearch, setShowSearch] = useAtom(showSearchAtom);
  const isMobile = useAtomValue(isMobileAtom);
  // With the relaunch, home is the directory: the box reads like /people's and
  // only takes focus on desktop, where it doesn't pop a keyboard.
  const {isAskRelaunchEnabled: isDirectory} = useAtomValue(growthbookCacheAtom);
  const searchFieldRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleMouseDown = (event: globalThis.MouseEvent) => {
      const activeElement = document.activeElement;
      if (!(activeElement instanceof HTMLElement) || activeElement.tagName !== 'INPUT') {
        return;
      }

      if (
        searchFieldRef.current?.contains(activeElement) &&
        !searchFieldRef.current.contains(event.target as Node)
      ) {
        activeElement.blur();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, []);

  const openSearch = () => {
    logDashboardInteraction({action: 'search_open'});
    setShowSearch(source);
  };

  return (
    <>
      {/* With the relaunch, the search box opens right on top of this field. */}
      <div ref={searchFieldRef} data-search-anchor>
        {isDirectory ? (
          // Drawn like the box's own header, so opening it doesn't shift a thing;
          // the form row keeps today's gap above the chips.
          <FormRow>
            <HomeSearchField onOpen={openSearch} autoFocus={!showSearch && !isMobile} />
          </FormRow>
        ) : (
          <FormRow>
            <TextInput
              width="100%"
              onClick={openSearch}
              onKeyDown={(e) => {
                e.stopPropagation();

                if (
                  e.metaKey ||
                  e.ctrlKey ||
                  e.altKey ||
                  e.key.startsWith('Arrow') ||
                  ['Tab', 'Escape', 'Shift', 'Enter'].includes(e.key)
                ) {
                  return;
                }

                openSearch();
              }}
              size="large"
              autoFocus={!showSearch}
              placeholder={t('search_global_short')}
              withLeftAccessory={
                <UIIcon size="large" src={AiSearchLine} className={atoms({color: 'Text Subtle'})} />
              }
            />
          </FormRow>
        )}
      </div>

      <KnowledgeChipsContainer />
    </>
  );
};

const maxLabelLength = 35;
export const getKnowledgeLinkIcon = (icon: string) =>
  KnowledgeLinkIcons[icon as keyof typeof KnowledgeLinkIcons] ?? LinkLine;

type HomeChipItem = {
  id: string;
  label: string;
  icon: UIIconProps['src'];
};

type KnowledgeChipItem = HomeChipItem &
  (
    | {
        chipType: string;
        type: 'url' | 'external_url';
        url: string;
        /** Set when the chip asks Ask this instead of opening `url`. */
        question?: string;
        /** The Knowledge page `url` opens, whose articles Ask reads for `question`. */
        knowledgePageId?: string;
      }
    | {
        chipType: string;
        type: 'ask_query';
      }
  );

type KnowledgeOverflowControls = {
  left: boolean;
  right: boolean;
};

/**
 * The question a topic chip asks when chips ask Ask. Only knowledge pages inside
 * CompanyOS (`/content/...`) ask, since Ask answers them for the asker's country
 * and worker type; tools and app pages stay links.
 */
export const knowledgeChipQuestion = ({
  title,
  url,
  link_type,
}: Pick<KnowledgeLink, 'title' | 'url' | 'link_type'>): string | undefined => {
  if ((link_type ?? 'url') !== 'url' || !url.startsWith('/content/')) {
    return undefined;
  }
  const topic = title.trim().replace(/\s+FAQs?$/i, '');
  return `What do I need to know about ${topic}?`;
};

/**
 * The page id in a `/content/<category>/<id>` chip url (an article or a topic),
 * or the category for a `/content/<category>` page, sent with the chip's
 * question so Ask answers from that page's articles instead of searching for
 * them. A page with nothing to read (a category) gets its search done up front.
 */
export const knowledgeChipPageId = (url: string): string | undefined => {
  const [path] = url.split(/[?#]/);
  const match = /^\/content\/([^/]+)(?:\/([^/]+))?\/?$/.exec(path);
  return match ? decodeURIComponent(match[2] ?? match[1]) : undefined;
};

const buildKnowledgeChipItems = (
  links: KnowledgeLink[],
  relaunch: boolean
): KnowledgeChipItem[] => {
  return links
    .filter((link) => link.title && (link.link_type === 'ask_query' || link.url))
    .map((link): KnowledgeChipItem => {
      const linkType = link.link_type ?? 'url';

      if (linkType === 'ask_query') {
        return {
          id: `ask-${link.title}`,
          label: link.title,
          // The relaunch marks Ask chips with the Ask logo.
          icon: relaunch ? CompanyAskGlyph : getKnowledgeLinkIcon(link.icon),
          chipType: link.chip_type,
          type: linkType,
        };
      }

      const question = relaunch ? knowledgeChipQuestion(link) : undefined;
      return {
        id: link.url,
        label: link.title,
        icon: getKnowledgeLinkIcon(link.icon),
        chipType: link.chip_type,
        type: linkType,
        url: link.url,
        question,
        knowledgePageId: question ? knowledgeChipPageId(link.url) : undefined,
      };
    });
};

/**
 * What an Ask chip does with its title: one ending in "…?" ("Who owns…?") starts
 * that question in the composer for you to finish, a full question is asked,
 * and anything else just opens Ask.
 */
export const askChipAction = (
  title: string
): {kind: 'seed'; seed: string} | {kind: 'ask'; question: string} | {kind: 'open'} => {
  const trimmed = title.trim();
  const stem = trimmed.replace(/\s*(…|\.\.\.)\?$/, '');
  if (stem !== trimmed && stem) {
    return {kind: 'seed', seed: `${stem} `};
  }
  return trimmed.endsWith('?') ? {kind: 'ask', question: trimmed} : {kind: 'open'};
};

/** Search shows this many topic chips (about two rows) until "+N" is clicked. */
const SEARCH_CHIP_LIMIT = 8;

const getKnowledgeRailMaskImage = ({left, right}: KnowledgeOverflowControls) => {
  if (left && right) {
    return 'linear-gradient(to right, transparent 0, #000 40px, #000 calc(100% - 40px), transparent 100%)';
  }

  if (left) {
    return 'linear-gradient(to right, transparent 0, #000 40px, #000 100%)';
  }

  if (right) {
    return 'linear-gradient(to right, #000 0, #000 calc(100% - 40px), transparent 100%)';
  }

  return undefined;
};

const getKnowledgeOverflowControls = (
  knowledgeRail: HTMLDivElement,
  scrollLeft = knowledgeRail.scrollLeft
): KnowledgeOverflowControls => {
  const hasOverflow = knowledgeRail.scrollWidth > knowledgeRail.clientWidth + 1;
  const maxScrollLeft = Math.max(knowledgeRail.scrollWidth - knowledgeRail.clientWidth, 0);
  const boundedScrollLeft = Math.min(Math.max(scrollLeft, 0), maxScrollLeft);

  return {
    left: hasOverflow && boundedScrollLeft > 1,
    right: hasOverflow && boundedScrollLeft < maxScrollLeft - 1,
  };
};

/**
 * The admin-managed knowledge chips. `surface` says where they're shown: a
 * scrolling rail under the home search box, or a wrapping list under `title`
 * in the global search panel's empty state, where a click also closes the panel.
 */
export const KnowledgeChipsContainer = ({
  surface = 'home',
  title,
}: {
  surface?: 'home' | 'search';
  title?: ReactNode;
}) => {
  const setAiDrawerView = useSetAtom(aiDrawerViewAtom);
  const setComposerDraft = useSetAtom(aiSearchComposerDraftAtom);
  const openAiSearchAndAsk = useSetAtom(openAiSearchAndAskAtom);
  const setShowSearch = useSetAtom(showSearchAtom);
  const [showAllChips, setShowAllChips] = useState(false);
  const {data, isLoading} = useQuery({
    queryKey: HOME_KNOWLEDGE_LINKS_QUERY_KEY,
    queryFn: () => getService(HomeService).knowledgeLinksApiV1HomeKnowledgeLinksGet(),
    staleTime: KNOWLEDGE_LINKS_STALE_TIME_MS,
  });

  const links = useMemo(() => data ?? [], [data]);
  const {isAskRelaunchEnabled} = useAtomValue(growthbookCacheAtom);
  const knowledgeChips = useMemo(
    () => buildKnowledgeChipItems(links, isAskRelaunchEnabled),
    [links, isAskRelaunchEnabled]
  );
  const knowledgeRailRef = useRef<HTMLDivElement>(null);
  const [knowledgeOverflowControls, setKnowledgeOverflowControls] = useState({
    left: false,
    right: false,
  });
  const knowledgeRailMaskImage = getKnowledgeRailMaskImage(knowledgeOverflowControls);

  const updateKnowledgeOverflowControls = useCallback(
    (knowledgeRail: HTMLDivElement, scrollLeft?: number) => {
      setKnowledgeOverflowControls(getKnowledgeOverflowControls(knowledgeRail, scrollLeft));
    },
    []
  );

  const handleKnowledgeOverflow = useCallback(() => {
    const knowledgeRail = knowledgeRailRef.current;
    if (!knowledgeRail) {
      setKnowledgeOverflowControls({left: false, right: false});
      return;
    }

    updateKnowledgeOverflowControls(knowledgeRail);
  }, [updateKnowledgeOverflowControls]);

  const handleKnowledgeRailScroll = useCallback(
    (event: ReactUIEvent<HTMLDivElement>) => {
      updateKnowledgeOverflowControls(event.currentTarget);
    },
    [updateKnowledgeOverflowControls]
  );

  useEffect(() => {
    handleKnowledgeOverflow();
  }, [handleKnowledgeOverflow, knowledgeChips]);

  useEffect(() => {
    const knowledgeRail = knowledgeRailRef.current;
    if (!knowledgeRail) {
      return;
    }

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(handleKnowledgeOverflow) : null;
    resizeObserver?.observe(knowledgeRail);
    if (knowledgeRail.parentElement) {
      resizeObserver?.observe(knowledgeRail.parentElement);
    }

    return () => {
      resizeObserver?.disconnect();
    };
  }, [handleKnowledgeOverflow]);

  if (isLoading) {
    return surface === 'search' ? null : <Box style={{height: 24}} />;
  }

  if (!knowledgeChips.length) {
    return null;
  }

  const handleKnowledgeChipClick = (knowledgeChipItem: KnowledgeChipItem) => {
    if (surface === 'search') {
      setShowSearch(false);
    }
    const asks = knowledgeChipItem.type === 'ask_query' || !!knowledgeChipItem.question;
    analyticsLogger().logEvent('HOME_RECENT_SEARCH', {
      chipType: knowledgeChipItem.chipType,
      source: 'knowledge_chip',
      surface,
      linkType: knowledgeChipItem.type,
      action: asks ? 'ask' : 'link',
    });
    if (knowledgeChipItem.type !== 'ask_query') {
      if (knowledgeChipItem.question) {
        openAiSearchAndAsk(
          knowledgeChipItem.question,
          surface === 'search' ? 'search_knowledge_chip' : 'home_knowledge_chip',
          {knowledgePageId: knowledgeChipItem.knowledgePageId}
        );
      }
      return;
    }
    const action: ReturnType<typeof askChipAction> = isAskRelaunchEnabled
      ? askChipAction(knowledgeChipItem.label)
      : {kind: 'open'};
    if (action.kind === 'ask') {
      openAiSearchAndAsk(action.question, 'home');
      return;
    }
    if (action.kind === 'seed') {
      setComposerDraft(action.seed);
    }
    setAiDrawerView('expanded');
  };

  if (surface === 'search') {
    const visibleChips = showAllChips ? knowledgeChips : knowledgeChips.slice(0, SEARCH_CHIP_LIMIT);
    const hiddenCount = knowledgeChips.length - visibleChips.length;
    return (
      <Box>
        {title}
        <Split gap="8" wrap="wrap" paddingX="8">
          {visibleChips.map((knowledgeChipItem) => (
            <Split.Item key={knowledgeChipItem.id}>
              <KnowledgeChip
                knowledgeChipItem={knowledgeChipItem}
                onClick={handleKnowledgeChipClick}
              />
            </Split.Item>
          ))}
          {hiddenCount > 0 && (
            <Split.Item>
              <Chip
                size="small"
                onClick={() => setShowAllChips(true)}
                aria-label={t('search_popular_more', {count: hiddenCount})}
              >
                <Chip.Content>{`+${hiddenCount}`}</Chip.Content>
              </Chip>
            </Split.Item>
          )}
        </Split>
      </Box>
    );
  }

  const handleKnowledgeOverflowClick = (direction: 'left' | 'right') => {
    const knowledgeRail = knowledgeRailRef.current;
    if (!knowledgeRail) {
      return;
    }

    const scrollDistance = Math.max(knowledgeRail.clientWidth * 0.75, 160);
    const scrollLeft = direction === 'left' ? -scrollDistance : scrollDistance;
    const targetScrollLeft = knowledgeRail.scrollLeft + scrollLeft;
    updateKnowledgeOverflowControls(knowledgeRail, targetScrollLeft);
    window.requestAnimationFrame(() => {
      knowledgeRail.scrollBy?.({left: scrollLeft, behavior: 'smooth'});
      if (!knowledgeRail.scrollBy) {
        knowledgeRail.scrollLeft += scrollLeft;
      }
    });
  };

  return (
    <Box display="flex" alignItems="center" style={{gap: 8, minWidth: 0, position: 'relative'}}>
      {knowledgeOverflowControls.left && (
        <IconButton
          variant="transparent"
          shape="circular"
          size="small"
          onClick={() => handleKnowledgeOverflowClick('left')}
          aria-label="Show previous knowledge links"
          className={styles.knowledgeOverflowButton}
        >
          <UIIcon src={ChevronLeftLine} className={atoms({color: 'Text Subtle'})} />
        </IconButton>
      )}
      <div
        ref={knowledgeRailRef}
        data-testid="knowledge-chip-rail"
        className={styles.knowledgeChipRail}
        onScroll={handleKnowledgeRailScroll}
        style={{
          maskImage: knowledgeRailMaskImage,
          WebkitMaskImage: knowledgeRailMaskImage,
        }}
      >
        {knowledgeChips.map((knowledgeChipItem) => (
          <div
            id={`chip-${knowledgeChipItem.label}`}
            key={knowledgeChipItem.id}
            style={{flex: '0 0 auto'}}
          >
            <KnowledgeChip
              knowledgeChipItem={knowledgeChipItem}
              onClick={handleKnowledgeChipClick}
            />
          </div>
        ))}
      </div>

      {knowledgeOverflowControls.right && (
        <IconButton
          variant="transparent"
          shape="circular"
          size="small"
          onClick={() => handleKnowledgeOverflowClick('right')}
          aria-label="Show more knowledge links"
          className={styles.knowledgeOverflowButton}
        >
          <UIIcon src={ChevronRightLine} className={atoms({color: 'Text Subtle'})} />
        </IconButton>
      )}
    </Box>
  );
};

// URL chips are real anchors so right-click / cmd-click can open them in a new tab. A plain click
// stays in this tab: internal routes navigate client-side, external urls load in place. Chips that
// ask Ask are buttons.
const KnowledgeChip = ({
  knowledgeChipItem,
  onClick,
}: {
  knowledgeChipItem: KnowledgeChipItem;
  onClick: (knowledgeChipItem: KnowledgeChipItem) => void;
}) => {
  if (knowledgeChipItem.type === 'ask_query' || knowledgeChipItem.question) {
    return <HomeChip homeChipItem={knowledgeChipItem} onClick={onClick} />;
  }

  const chip = <HomeChip homeChipItem={knowledgeChipItem} />;
  const handleClick = () => onClick(knowledgeChipItem);
  // Middle-click opens a new tab without firing click, so log it from auxclick.
  const handleAuxClick = (event: ReactMouseEvent) => {
    if (event.button === 1) {
      handleClick();
    }
  };

  if (knowledgeChipItem.type === 'external_url') {
    // DSYS Link forces target="_blank" for absolute urls, so external chips use a plain anchor.
    return (
      <a
        href={knowledgeChipItem.url}
        aria-label={knowledgeChipItem.label}
        className={styles.knowledgeChipLink}
        onClick={handleClick}
        onAuxClick={handleAuxClick}
      >
        {chip}
      </a>
    );
  }

  return (
    <Link
      to={knowledgeChipItem.url}
      state={{source: 'home-chip'}}
      aria-label={knowledgeChipItem.label}
      monochromatic
      hasNoUnderline
      className={styles.knowledgeChipLink}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
    >
      {chip}
    </Link>
  );
};

// Without onClick the chip is presentational and its parent link handles focus and clicks.
const HomeChip = <T extends HomeChipItem>({
  onClick,
  homeChipItem,
}: {
  onClick?: (homeChipItem: T) => void;
  homeChipItem: T;
}) => {
  const {icon, label} = homeChipItem;
  return (
    <Chip
      size="small"
      onClick={onClick ? () => onClick(homeChipItem) : undefined}
      tabIndex={onClick ? undefined : -1}
      aria-hidden={onClick ? undefined : true}
    >
      <Chip.IconAccessory>
        <UIIcon src={icon} />
      </Chip.IconAccessory>
      <Chip.Content>
        {label.length > maxLabelLength ? `${label.slice(0, maxLabelLength)}...` : label}
      </Chip.Content>
    </Chip>
  );
};
